import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, Input, OnInit, Output, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { TenantService } from '../services/tenant.service';
import { ManagedRole } from '../models/member.model';
import { ToastService } from '../../../../shared/services/toast-service';
import { Modal } from '../../../../shared/components/modal/modal';
import { ModalFooterDirective } from '../../../../shared/components/modal/modal-footer.directive';
import { FormField } from '../../../../shared/components/forms/form-field';
import { TextInput } from '../../../../shared/components/forms/text-input';
import { Select, SelectOption } from '../../../../shared/components/forms/select';

/**
 * Invite-user form — POST /organizations/{id}/members. Creates the account
 * pending password setup and sends the action email; the invitee appears in
 * the member list as INVITED. The email IS the username (EmailAsUsername).
 * The role is required and picked from GET /organizations/{id}/roles; if that
 * fetch fails the field degrades to a free-text input so invites still work.
 */
@Component({
  selector: 'app-invite-member-dialog',
  standalone: true,
  imports: [FormsModule, TranslocoDirective, Modal, ModalFooterDirective, FormField, TextInput, Select],
  templateUrl: './invite-member-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InviteMemberDialogComponent implements OnInit {
  @Input({ required: true }) tenantId!: string;
  @Input() tenantName?: string;
  @Output() close = new EventEmitter<void>();
  @Output() sent = new EventEmitter<void>();

  private readonly service = inject(TenantService);
  private readonly toastService = inject(ToastService);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  readonly open = signal(true);
  readonly isSending = signal(false);

  /** null while loading; the select stays disabled until options arrive. */
  private readonly roles = signal<ManagedRole[] | null>(null);
  /** Roles endpoint failed — degrade to the free-text role input. */
  readonly rolesUnavailable = signal(false);
  readonly rolesLoading = computed(() => this.roles() === null);
  readonly roleOptions = computed<SelectOption[]>(() =>
    (this.roles() ?? []).map((r) => ({ value: r.name, label: r.name })),
  );

  email = '';
  firstName = '';
  lastName = '';
  readonly role = signal<string | null>(null);

  /** Description of the currently selected role, shown below the select. */
  readonly selectedRoleDescription = computed<string | null>(
    () => this.roles()?.find((r) => r.name === this.role())?.description || null,
  );

  ngOnInit(): void {
    this.service
      .getAssignableRoles(this.tenantId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (roles) => this.roles.set(roles),
        error: () => {
          this.rolesUnavailable.set(true);
          this.toastService.show(this.transloco.translate('admin.tenants.toast.rolesLoadFailed'), 'error');
        },
      });
  }

  get isValid(): boolean {
    const email = this.email.trim();
    return email.includes('@') && !email.includes(' ') && !!this.role()?.trim();
  }

  onSubmit(): void {
    if (!this.isValid || this.isSending()) return;
    this.isSending.set(true);
    this.service
      .inviteMember(this.tenantId, {
        email: this.email.trim(),
        firstName: this.firstName.trim() || undefined,
        lastName: this.lastName.trim() || undefined,
        realmRoles: [this.role()!.trim()],
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show(this.transloco.translate('admin.tenants.toast.invited'), 'success');
          this.isSending.set(false);
          this.sent.emit();
        },
        error: () => {
          this.toastService.show(this.transloco.translate('admin.tenants.toast.inviteFailed'), 'error');
          this.isSending.set(false);
        },
      });
  }

  onClose(): void {
    this.close.emit();
  }
}
